import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { ApiError } from "./api";
import { streamChat } from "./sse";

export interface LiveStream {
  /** Optimistic user text shown until the server transcript is refetched. */
  pendingUser?: string;
  text: string;
  thinking: string;
  answeredBy?: { providerId: string; providerName: string; model: string };
  fallbacks: Array<{ fromModel: string; toModel: string; reason: string }>;
  error?: string;
  usage?: { inputTokens: number; outputTokens: number; costUsd: number };
  running: boolean;
}

interface ChatState {
  streams: Record<string, LiveStream>;
  patch: (id: string, fn: (s: LiveStream) => LiveStream) => void;
  clear: (id: string) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  streams: {},
  patch: (id, fn) =>
    set((st) => ({
      streams: {
        ...st.streams,
        [id]: fn(st.streams[id] ?? { text: "", thinking: "", fallbacks: [], running: true }),
      },
    })),
  clear: (id) =>
    set((st) => {
      const { [id]: _drop, ...rest } = st.streams;
      return { streams: rest };
    }),
}));

const controllers = new Map<string, AbortController>();

export function stopStream(conversationId: string) {
  controllers.get(conversationId)?.abort();
}

/** Runs one assistant turn. State lives in the store so it survives route changes. */
export async function runTurn(
  conversationId: string,
  body: Record<string, unknown>,
  qc: QueryClient,
  pendingUser?: string,
) {
  const { patch, clear } = useChatStore.getState();
  const ac = new AbortController();
  let fatal = false;
  controllers.set(conversationId, ac);
  patch(conversationId, () => ({
    pendingUser,
    text: "",
    thinking: "",
    fallbacks: [],
    running: true,
  }));
  try {
    for await (const ev of streamChat(conversationId, body, ac.signal)) {
      switch (ev.event) {
        case "start":
          // A fallback restarts the answer: discard partial state from the failed attempt.
          patch(conversationId, (s) => ({ ...s, answeredBy: ev.data, text: "", thinking: "" }));
          break;
        case "fallback":
          patch(conversationId, (s) => ({
            ...s,
            fallbacks: [
              ...s.fallbacks,
              { fromModel: ev.data.fromModel, toModel: ev.data.toModel, reason: ev.data.reason },
            ],
          }));
          break;
        case "text":
          patch(conversationId, (s) => ({ ...s, text: s.text + ev.data.delta }));
          break;
        case "thinking":
          patch(conversationId, (s) => ({ ...s, thinking: s.thinking + ev.data.delta }));
          break;
        case "usage":
          patch(conversationId, (s) => ({ ...s, usage: ev.data }));
          break;
        case "error":
          patch(conversationId, (s) => ({ ...s, error: ev.data.message }));
          break;
        case "done":
          if (ev.data.title) qc.invalidateQueries({ queryKey: ["conversations"] });
          break;
      }
    }
  } catch (e) {
    if (!(e instanceof DOMException && e.name === "AbortError")) {
      fatal = true;
      const msg = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      patch(conversationId, (s) => ({ ...s, error: msg }));
    }
  } finally {
    controllers.delete(conversationId);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["conversation", conversationId] }),
      qc.invalidateQueries({ queryKey: ["conversations"] }),
    ]);
    // Stream-level errors are persisted server-side (shown from the transcript). Request-level
    // failures (no model, rate limit, network) never reached the transcript, so keep them visible.
    if (fatal) patch(conversationId, (x) => ({ ...x, running: false }));
    else clear(conversationId);
  }
}
